/**
 * @fileoverview Events Controller Module.
 * Manages the behavior and interactions of the events section on the page,
 * including carousel functionality, event filtering, and dynamic content loading.
 */

import { elementExists, adjustTitleHeights, closeAllDropdowns, addParamToUrl, getAjaxUrl } from "../Utils";
import Glide from "@glidejs/glide";

const EventsController = {
    eventsContainer: '.events-js',
    desktopSelectedEventClass: 'border-b-4',
    copyButtonSelector: '.copy-btn',
    eventsContainerId: 'events-container-upcoming',
    noEventsTextId: 'no-events-text-js',
    loadMoreId: 'load-more',
    buttonTextSelector: '.button-text',
    gridSelector: '.grid',
    seeMoreUpcomingEventsSelector: '.see-more-upcoming-events-js',
    glideSelector: '.glide',
    carouselArrow: '.carousel-arrow-js',
    defaultFiltersLabel: 'All events',
    filtersLabel: '.dropdown-label-js',
    glide: undefined,
    ajaxUrl: getAjaxUrl(),

    /**
     * Initializes the EventsController.
     * Sets up the header carousel, event listeners, and adjusts content if necessary.
     */
    init() {
        if (!elementExists(this.eventsContainer)) return false;
        adjustTitleHeights();
        this.setListeners();
        this.initHeaderCarousel();
    },

    /**
     * Sets up various event listeners related to the events section.
     */
    setListeners() {
        let copyButtons = document.querySelectorAll(this.copyButtonSelector);
        copyButtons.forEach((button) => {
            button.addEventListener('click', (ev) => this.copyToClipboard(ev));
        });

        const filters = document.querySelectorAll("[data-slug]");
        filters.forEach(link => {
            link.addEventListener('click', (ev) => this.clickFilterHandler(ev, filters));
        });

        const loadMoreBtn = document.getElementById(this.loadMoreId);
        if (!loadMoreBtn) return;
        loadMoreBtn.addEventListener('click', () => this.loadMoreClickHandler(loadMoreBtn));

        const sliderButtons = document.querySelectorAll(this.carouselArrow);
        if (sliderButtons && sliderButtons.length) {
            sliderButtons.forEach(button => {
                button.addEventListener('click', (ev) => this.sliderButtonClickHandler(ev));
            });
        }
    },

    /**
     * Handler for the slider button click event in the carousel.
     * @param {Event} ev - The event object.
     */
    sliderButtonClickHandler(ev) {
        ev.preventDefault();
        const button = ev.target.closest(this.carouselArrow);
        if (!button) return

        const direction = button.dataset.glideDir;
        if (!direction) return

        this.glide.go(direction);
    },

    /**
     * Copies a specific link to the clipboard when a copy button is clicked.
     * @param {Event} ev - The event object.
     */
    copyToClipboard(ev) {
        ev.preventDefault();
        ev.stopPropagation();
        const button = ev.target.closest(this.copyButtonSelector);
        if (!button) return;

        const link = button.getAttribute('data-link');
        const textArea = document.createElement('textarea');
        const buttonText = button.querySelector(this.buttonTextSelector);

        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        buttonText.textContent = "Copied"
        setTimeout(() => {
            buttonText.textContent = "Copy link";
        }, 2000);
    },

    /**
     * Handles click events on filter links.
     * @param {Event} ev - The event object.
     * @param {NodeList} filters - Collection of filter elements.
     */
    clickFilterHandler(ev, filters) {
        ev.preventDefault();
        let slug = ev.srcElement.getAttribute('data-slug');
        this.setActiveFilter(ev, filters, slug);
        this.updateMobileFilterLabel(slug);
        this.fetchFilteredEvents(slug);
        addParamToUrl('event_type', slug);
    },

    /**
     * Sets an active filter based on the clicked filter element.
     * @param {Event} ev - The event object.
     * @param {NodeList} filters - Collection of filter elements.
     * @param {string} slug - The data attribute representing the filter.
     */
    setActiveFilter(ev, filters, slug) {
        filters.forEach(link => {
            const itemSlug = link.getAttribute('data-slug');
            if (link.classList.contains('desktop-js')) {
                if (itemSlug === slug) {
                    link.parentElement.classList.add(this.desktopSelectedEventClass);
                } else {
                    link.parentElement.classList.remove(this.desktopSelectedEventClass);
                }
            } else if (link.classList.contains('mobile-js')) {
                if (itemSlug === slug) {
                    link.classList.add('selected');
                } else {
                    link.classList.remove('selected');
                }
            }
        });
        closeAllDropdowns();
    },

    /**
     * Updates the label for the mobile filter dropdown based on the selected filter.
     * @param {string} slug - The data attribute representing the filter.
     */
    updateMobileFilterLabel(slug) {
        const dropdownLabelElement = document.querySelector(this.filtersLabel);
        if (!dropdownLabelElement) return;
        if (slug === 'all') {
            dropdownLabelElement.textContent = this.defaultFiltersLabel;
        } else {
            dropdownLabelElement.textContent = slug;
        }
    },



    /**
     * Fetches events based on the provided slug (event type).
     * Makes an asynchronous request to retrieve filtered events data.
     * @param {string} slug - The event type to filter the events.
     */
    fetchFilteredEvents(slug) {
        let data = new FormData();
        data.append('action', 'fetch_filtered_events');
        data.append('event_type', slug);

        fetch(this.ajaxUrl, {
            method: 'POST',
            body: data
        })
            .then(response => response.json())
            .then(data => this.fetchFilteredEventsSuccess(data))
            .catch(error => console.error('Error fetching events:', error));
    },

    /**
     * Handles the successful response of the fetchFilteredEvents function.
     * Updates the events container with the fetched events data.
     * @param {Object} data - The data returned from the fetch request, containing events and a flag indicating if more events are available.
     */
    fetchFilteredEventsSuccess(data) {
        let { events, hasMore } = data;
        const hasEvents = events.length > 0;
        events = events.filter(event => event !== null);
        const eventsContainer = document.getElementById(this.eventsContainerId);
        this.showHideNoEventsText(hasEvents);
        this.showHideEventsContainer(eventsContainer, hasEvents);
        eventsContainer.innerHTML = '';
        events.forEach(event => {
            let eventDiv = document.createElement('div');
            let endDateHtml = event.end_date ? `
                                <div class="pl-5 border-l border-pxl-white-25">
                                    <div class="font-semibold text-heading-medium">
                                        ${event.end_date}
                                    </div>
                                    <div class="opacity-80">
                                        End Date
                                    </div>
                                </div>
                            ` : '';
            eventDiv.innerHTML = `
                <a href="${event.permalink}" class="content-card-link">
                    <span class="top-cnt">
                        <span class="img-wrap" style="background-image: url('${event.featured_image}');"> 
                            <span class="tags-wrap">
                                <span class="tag">${event.event_type}</span>
                                <!-- .tag -->
                            </span>
                            <!-- .tags-wrap -->
                        </span>
                        <!-- .img-wrap -->
                    </span>
                    <!-- .top-cnt -->
                
                    <span class="bottom-cnt">
                        <h3>${event.title}</h3>
                        <span class="card-meta">
                            <span class="meta meta-date">${event.start_date}</span>
                        </span>
                        <p>${event.excerpt}</p>
                    </span>
                    <!-- .bottom-cnt -->
                </a>
                `;
            eventsContainer.appendChild(eventDiv);
            this.seeMoreButtonActions(hasMore);
        });
    },

    /**
     * Toggles visibility of the events container element based on the availability of more events.
     *
     * @param {HTMLElement} eventsContainerEl - The HTML element that contains the events.
     * @param {boolean} hasMore - Flag indicating whether there are more events to show.
     */
    showHideEventsContainer(eventsContainerEl, hasMore) {
        if (hasMore) {
            eventsContainerEl.classList.remove('hidden');
        } else {
            eventsContainerEl.classList.add('hidden');
        }
    },

    /**
     * Toggles visibility of the 'no events' text based on the existence of events.
     *
     * @param {boolean} hasEvents - Flag indicating whether there are events to display.
     */
    showHideNoEventsText(hasEvents) {
        const noEventsText = document.getElementById(this.noEventsTextId);
        if (noEventsText) {
            if (hasEvents) {
                noEventsText.classList.add('hidden');
            } else {
                noEventsText.classList.remove('hidden');
            }
        }
    },

    /**
     * Updates the visibility of the 'See More' button based on whether more events are available.
     * @param {boolean} hasMore - Flag indicating if more events are available to load.
     */
    seeMoreButtonActions(hasMore) {
        const seeMoreButton = document.querySelectorAll(this.seeMoreUpcomingEventsSelector)[0];
        if (seeMoreButton) {
            seeMoreButton.setAttribute('data-page', '2');
            if (hasMore) {
                seeMoreButton.classList.remove('hidden');
            } else {
                seeMoreButton.classList.add('hidden');
            }
        }
    },

    /**
     * Initializes the Glide.js header carousel.
     */
    initHeaderCarousel() {
        if (!elementExists(this.glideSelector)) return;
        this.glide = new Glide(this.glideSelector, {
            type: 'carousel',
            startAt: 0,
        });

        this.glide.mount();
    },

    /**
     * Handler for the 'Load More' button click event.
     * Fetches and appends additional events to the list.
     * @param {HTMLElement} loadMoreBtn - The 'Load More' button element.
     */
    loadMoreClickHandler(loadMoreBtn) {
        const eventType = this.getEventTypeFromUrl();
        let page = parseInt(loadMoreBtn.getAttribute('data-page'));
        let data = new FormData();
        data.append('action', 'load_more_events');
        data.append('page', page);
        data.append('event_type', eventType);
        fetch(this.ajaxUrl, {
            method: 'POST',
            body: data
        })
            .then(response => response.json())
            .then(data => this.loadMoreClickHandlerSuccess(data, loadMoreBtn, page))
            .catch(error => console.error('Error:', error));
    },

    /**
     * Success handler for loading more events.
     * Updates the UI with additional events.
     * @param {Object} data - The data returned from the fetch request.
     * @param {HTMLElement} loadMoreBtn - The 'Load More' button element.
     * @param {number} page - The current page number.
     */
    loadMoreClickHandlerSuccess(data, loadMoreBtn, page) {
        const { has_more, html } = data;
        const grid = document.querySelector(this.gridSelector);
        grid.insertAdjacentHTML('beforeend', html);
        loadMoreBtn.setAttribute('data-page', page + 1);
        this.hideShowLoadMoreButton(has_more, loadMoreBtn)
    },

    /**
     * Shows or hides the 'Load More' button based on the availability of more events.
     * @param {boolean} hasMore - Indicates if more events are available to load.
     * @param {HTMLElement} loadMoreBtn - The 'Load More' button element.
     */
    hideShowLoadMoreButton(hasMore, loadMoreBtn) {
        if (!hasMore) {
            loadMoreBtn.classList.add('hidden');
        } else {
            loadMoreBtn.classList.remove('hidden');
        }
    },

    /**
     * Retrieves the event type from the URL parameters.
     * @returns {string} The event type parameter value.
     */
    getEventTypeFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('event_type') ?? 'all';
    }
};

export default EventsController;